import React, { useState, useMemo } from 'react';
import { categories } from '../data';
import { useProducts } from '../hooks/useProducts';
import ProductCard from '../components/ProductCard';
import './ProductsPage.css';

const SORT_OPTIONS = [
  { value: 'popular',    label: '🔥 Most Popular' },
  { value: 'rating',     label: '⭐ Top Rated' },
  { value: 'price-asc',  label: '💰 Price: Low → High' },
  { value: 'price-desc', label: '💎 Price: High → Low' },
  { value: 'newest',     label: '🆕 Newest First' },
];

export default function ProductsPage({ navigate }) {
  const { products, fbLoading } = useProducts();

  const [activeCategory, setActiveCategory] = useState('all');
  const [search,         setSearch]         = useState('');
  const [sortBy,         setSortBy]         = useState('popular');
  const [viewMode,       setViewMode]       = useState('grid');
  const [priceRange,     setPriceRange]     = useState([0, 1000]);
  const [onlyOrganic,    setOnlyOrganic]    = useState(false);
  const [onlyInStock,    setOnlyInStock]    = useState(false);
  const [activeTab,      setActiveTab]      = useState('all');
  const [wishlistIds] = useState(
    () => JSON.parse(localStorage.getItem('freshmart_wishlist') || '[]')
  );

  const maxPrice = products.length ? Math.max(...products.map(p => p.price || 0), 1000) : 1000;

  const filtered = useMemo(() => {
    let list = [...products];
    if (activeTab === 'wishlist') list = list.filter(p => wishlistIds.includes(p.id));
    if (activeCategory !== 'all') list = list.filter(p => p.category === activeCategory);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        p.name?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q) ||
        p.category?.toLowerCase().includes(q)
      );
    }
    list = list.filter(p => (p.price || 0) >= priceRange[0] && (p.price || 0) <= priceRange[1]);
    if (onlyOrganic) list = list.filter(p => p.badge === 'Organic' || p.category === 'organic');
    if (onlyInStock) list = list.filter(p => (p.stock || 0) > 0);

    if (sortBy === 'price-asc')  list = [...list].sort((a,b) => (a.price||0) - (b.price||0));
    else if (sortBy === 'price-desc') list = [...list].sort((a,b) => (b.price||0) - (a.price||0));
    else if (sortBy === 'rating')     list = [...list].sort((a,b) => (b.rating||0) - (a.rating||0));
    else if (sortBy === 'popular')    list = [...list].sort((a,b) => (b.reviews||0) - (a.reviews||0));
    else if (sortBy === 'newest')     list = [...list].sort((a,b) => (b.fromFirebase?1:0) - (a.fromFirebase?1:0));
    return list;
  }, [products, activeCategory, search, sortBy, priceRange, onlyOrganic, onlyInStock, activeTab, wishlistIds]);

  const resetFilters = () => {
    setActiveCategory('all'); setSearch(''); setSortBy('popular');
    setPriceRange([0, maxPrice]); setOnlyOrganic(false); setOnlyInStock(false);
  };

  return (
    <div className="page products-page">
      <div className="container">

        {/* Header */}
        <div className="products-hero">
          <div>
            <div className="products-breadcrumb">
              <button onClick={() => navigate('home')}>Home</button>
              <span>›</span>
              <span>{activeCategory === 'all' ? 'All Products' : categories.find(c=>c.id===activeCategory)?.name || activeCategory}</span>
            </div>
            <h1 className="page-title">🛒 Fresh Grocery Store</h1>
            <p className="page-sub">
              {fbLoading ? 'Loading products…' : `${products.length} products available`} · Farm fresh, delivered in 30 min
            </p>
          </div>
          <div className="products-tab-switcher">
            <button className={activeTab==='all'      ?'tab-active':''} onClick={()=>setActiveTab('all')}>
              🛒 All Products <span>{products.length}</span>
            </button>
            <button className={activeTab==='wishlist' ?'tab-active':''} onClick={()=>setActiveTab('wishlist')}>
              ❤️ Wishlist <span>{wishlistIds.length}</span>
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="products-toolbar">
          <div className="search-box-big">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              type="text"
              placeholder="Search products by name, type..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && <button className="search-clear" onClick={()=>setSearch('')}>✕</button>}
          </div>
          <div className="toolbar-right">
            <select className="sort-select-styled" value={sortBy} onChange={e=>setSortBy(e.target.value)}>
              {SORT_OPTIONS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <div className="view-toggle">
              <button className={viewMode==='grid'?'active':''} onClick={()=>setViewMode('grid')}>⊞</button>
              <button className={viewMode==='list'?'active':''} onClick={()=>setViewMode('list')}>☰</button>
            </div>
          </div>
        </div>

        <div className="products-layout">
          {/* Sidebar */}
          <aside className="filters-sidebar">
            <div className="filter-section">
              <h4>🗂 Categories</h4>
              <div className="filter-cats">
                {categories.map(cat => {
                  const count = cat.id === 'all'
                    ? products.length
                    : products.filter(p => p.category === cat.id).length;
                  return (
                    <button
                      key={cat.id}
                      className={`filter-cat-btn ${activeCategory===cat.id?'active':''}`}
                      onClick={() => setActiveCategory(cat.id)}
                    >
                      <div className="fcb-left">
                        <span className="fcb-icon">{cat.icon}</span>
                        <span className="fcb-name">{cat.name}</span>
                      </div>
                      <span className="fcb-count">{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="filter-section">
              <h4>💰 Price Range</h4>
              <div className="price-range-wrap">
                <div className="price-range-labels">
                  <span>₹{priceRange[0]}</span>
                  <span>₹{priceRange[1]}</span>
                </div>
                <input
                  type="range" min="0" max={maxPrice} value={priceRange[1]}
                  onChange={e => setPriceRange([priceRange[0], Number(e.target.value)])}
                  className="range-slider"
                />
                <div className="price-presets">
                  {[[0,100,'<₹100'],[0,250,'<₹250'],[0,500,'<₹500'],[0,maxPrice,'All']].map(([mn,mx,lbl])=>(
                    <button
                      key={lbl}
                      className={priceRange[0]===mn && priceRange[1]===mx ? 'preset-active' : ''}
                      onClick={() => setPriceRange([mn,mx])}
                    >{lbl}</button>
                  ))}
                </div>
              </div>
            </div>

            <div className="filter-section">
              <h4>🎛 Filters</h4>
              <label className="toggle-filter">
                <input type="checkbox" checked={onlyOrganic} onChange={e=>setOnlyOrganic(e.target.checked)} />
                <span className="toggle-slider" />
                <span>🌿 Organic Only</span>
              </label>
              <label className="toggle-filter">
                <input type="checkbox" checked={onlyInStock} onChange={e=>setOnlyInStock(e.target.checked)} />
                <span className="toggle-slider" />
                <span>✅ In Stock Only</span>
              </label>
            </div>

            <button className="clear-filters-btn" onClick={resetFilters}>🔄 Reset Filters</button>
          </aside>

          {/* Products Grid */}
          <div className="products-area">
            {/* Loading skeleton */}
            {fbLoading && (
              <div className="loading-banner">
                <span className="loading-spin">⏳</span> Loading products from store…
              </div>
            )}

            <div className="results-bar">
              <p>
                <strong>{filtered.length}</strong> products
                {activeCategory !== 'all' && ` in ${categories.find(c=>c.id===activeCategory)?.name}`}
              </p>
              {/* Active filter chips */}
              {(search || onlyOrganic || onlyInStock || priceRange[1] < maxPrice) && (
                <div className="active-filters">
                  {search && (
                    <span className="filter-tag">
                      "{search}" <button onClick={()=>setSearch('')}>✕</button>
                    </span>
                  )}
                  {onlyOrganic && (
                    <span className="filter-tag">
                      🌿 Organic <button onClick={()=>setOnlyOrganic(false)}>✕</button>
                    </span>
                  )}
                  {onlyInStock && (
                    <span className="filter-tag">
                      ✅ In Stock <button onClick={()=>setOnlyInStock(false)}>✕</button>
                    </span>
                  )}
                  {priceRange[1] < maxPrice && (
                    <span className="filter-tag">
                      Under ₹{priceRange[1]} <button onClick={()=>setPriceRange([0,maxPrice])}>✕</button>
                    </span>
                  )}
                </div>
              )}
            </div>

            {filtered.length === 0 && !fbLoading ? (
              <div className="no-results">
                <div className="no-results-img">🔍</div>
                <h3>No products found</h3>
                <p>Try adjusting your filters or search term</p>
                <button className="btn-primary" onClick={resetFilters}>Clear All Filters</button>
              </div>
            ) : (
              <div className={viewMode==='grid' ? 'products-grid' : 'products-list'}>
                {filtered.map(p => <ProductCard key={p.id || p.fireId} product={p} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
